import { ReactNode, useState } from "react";
import { AlertTriangle } from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { Button } from "../../components/Button/Button";
import { Input } from "../../components/Input/Input";
import { Modal } from "../../components/Modal/Modal";
import styles from "./ApplyConfirmModal.module.css";

/**
 * Icon tint of one operation row: destructive, risky, merely a write, or —
 * `info` — a write that has not happened yet and will happen without anybody
 * at the screen.
 */
export type OperationTone = "danger" | "warn" | "ok" | "info";

/** One line of the "here is what this actually does" list. */
export interface ApplyOperation {
  /** Stable key — the caller builds these from the plan. */
  id: string;
  tone: OperationTone;
  icon: ReactNode;
  /** May carry <b>/<i> emphasis, exactly like the mockup's list does. */
  label: ReactNode;
  /**
   * A muted second line under the label — what a scheduled package gives up to
   * the file (mockup L578). Its own field rather than markup inside `label`
   * because the line's tint and its break belong to the list, not the caller.
   */
  sub?: ReactNode;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** This environment's project name — the word that has to be typed. */
  projectName: string;
  /** What the apply will do, already filtered to the current selection. */
  operations: ApplyOperation[];
  /**
   * The half of it that does not happen yet: board updates parked until each
   * board's own next cut. Empty when nothing is scheduled, and then the modal
   * draws one unheaded list exactly as it always did — two headings over one
   * group would name a distinction this apply does not have.
   */
  scheduled?: ApplyOperation[];
  applying: boolean;
  onConfirm: () => void;
}

const TONE_CLASS: Record<OperationTone, string> = {
  danger: "",
  warn: styles.opIconWarn,
  ok: styles.opIconOk,
  info: styles.opIconInfo,
};

function OperationList({ operations }: { operations: ApplyOperation[] }) {
  return (
    <ul className={styles.opList}>
      {operations.map((op) => (
        <li key={op.id}>
          <span
            className={[styles.opIcon, TONE_CLASS[op.tone]]
              .filter(Boolean)
              .join(" ")}
            aria-hidden="true"
          >
            {op.icon}
          </span>
          <span className={styles.opText}>
            {op.label}
            {op.sub && <span className={styles.opSub}>{op.sub}</span>}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Type-to-confirm gate in front of the apply — mockup
 * .codelane/artifacts/t-27/transfer-import-diff.html L636-L682.
 *
 * The `DeleteProjectModal` idiom: a `danger` alert, then the operations
 * spelled out one per line so the human reads what is about to happen rather
 * than the word "apply", then the project name typed by hand. The confirm
 * button stays disabled until the typed value matches exactly — trimmed, but
 * never case-folded: the point of the gate is that the human looked.
 */
export function ApplyConfirmModal({ open, ...rest }: Props) {
  // Closing unmounts the gate, and with it the typed word: reopening starts
  // from a blank input, where a stale match would have handed back an enabled
  // danger button for free.
  if (!open) return null;
  return <ApplyConfirmGate {...rest} />;
}

function ApplyConfirmGate({
  onClose,
  projectName,
  operations,
  scheduled = [],
  applying,
  onConfirm,
}: Omit<Props, "open">) {
  const [typed, setTyped] = useState("");
  const hasScheduled = scheduled.length > 0;

  const matches = typed.trim() === projectName && projectName.length > 0;

  function handleClose() {
    if (applying) return;
    onClose();
  }

  return (
    <Modal
      open
      onClose={handleClose}
      title={`Apply snapshot to ${projectName}?`}
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={applying}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={onConfirm}
            disabled={!matches || applying}
            loading={applying}
          >
            Apply snapshot
          </Button>
        </>
      }
    >
      <Alert
        variant="danger"
        title="This is a live environment"
        icon={<AlertTriangle size={14} />}
      >
        {hasScheduled
          ? "There is no undo. What lands now, real players see the moment they reopen the client; what is scheduled lands without anyone at the screen."
          : "There is no undo. Real players will see these settings the moment they reopen the client."}
      </Alert>

      {hasScheduled ? (
        <>
          {operations.length > 0 && (
            <>
              <div className={styles.opGroup}>Applies the moment you confirm</div>
              <OperationList operations={operations} />
            </>
          )}
          <div className={styles.opGroup}>
            Scheduled — applies at each board&apos;s own next cut
          </div>
          <OperationList operations={scheduled} />
          <div className={styles.scheduleNote}>
            <Alert variant="warn">
              A scheduled package is re-validated at the cut. If a board has
              drifted by then, none of its package is applied, its next cycle is
              cut unchanged, and the board&apos;s Settings tab says why.
            </Alert>
          </div>
        </>
      ) : (
        <OperationList operations={operations} />
      )}

      <div className={styles.confirmLabel}>
        Type <b>{projectName}</b> to confirm
      </div>
      <Input
        aria-label={`Type ${projectName} to confirm`}
        placeholder={projectName}
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        autoFocus
      />
    </Modal>
  );
}
